import CircleFormController from '@/circle/components/CircleFormController'
import EditorController from '@/editor/components/EditorController'
import MeetingOriginBanner from '@/meeting/components/MeetingOriginBanner'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import useOrgMember from '@/member/hooks/useOrgMember'
import useWriteDecision from '../hooks/useWriteDecision'
import { useOrgContext } from '@/org/contexts/OrgContext'
import VisibilityField from '@/visibility/components/VisibilityField'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  UseModalProps,
  VStack,
} from '@chakra-ui/react'
import { DecisionFragment, Visibility_Enum } from '@gql'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  defaultVisibility,
  getVisibilityAccess,
} from '@rolebase/shared/model/visibility'
import { nameSchema } from '@rolebase/shared/schemas'
import React from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { track } from 'src/analytics'
import * as yup from 'yup'

interface Props extends UseModalProps {
  decision?: DecisionFragment
  defaults?: Partial<DecisionFragment>
  onCreate?(id: string): void
}

interface Values {
  title: string
  description: string
  circleId: string
  visibility: Visibility_Enum
}

const resolver = yupResolver(
  yup.object().shape({
    title: nameSchema.required(),
    circleId: yup.string().required(),
  })
)

export default function DecisionEditModal({
  defaults,
  decision,
  onCreate,
  ...modalProps
}: Props) {
  const { t } = useTranslation()
  const currentMember = useCurrentMember()
  // One write path, the backend door (GOUV-14)
  const writeDecision = useWriteDecision()
  const { orgId, orgData } = useOrgContext()

  const defaultValues = decision
    ? {
        title: decision.title,
        description: decision.description,
        circleId: decision.circleId,
        visibility: decision.visibility,
      }
    : {
        title: defaults?.title || '',
        description: defaults?.description || '',
        circleId: defaults?.circleId || '',
        visibility: defaults?.visibility ?? defaultVisibility.decision,
      }

  const formMethods = useForm<Values>({
    resolver,
    defaultValues,
  })
  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
  } = formMethods

  // Watch selected circle
  const circleId = watch('circleId')

  // The level chosen must leave the author able to read back what they save.
  // Writing itself is the backend's canEditRole (GOUV-14), not the level.
  // Display rule only, the Hasura permission decides (CONF-02).
  const visibility = watch('visibility')
  const isOrgWriter = useOrgMember()
  const isVisibilityAllowed =
    !circleId ||
    !orgData ||
    getVisibilityAccess({
      orgData,
      circleId,
      visibility,
      memberId: currentMember?.id,
      isOrgWriter,
    }).canRead

  const onSubmit = handleSubmit(async (values) => {
    if (!orgId || !currentMember) return
    if (decision) {
      await writeDecision(values, decision.id)
    } else {
      const newId = await writeDecision(values)
      track('decision_created', { visibility: values.visibility })
      onCreate?.(newId)
    }
    modalProps.onClose()
  })

  return (
    <FormProvider {...formMethods}>
      <Modal size="xl" {...modalProps}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={onSubmit}>
            <ModalHeader>
              {t(
                decision
                  ? 'DecisionEditModal.headingEdit'
                  : 'DecisionEditModal.headingCreate'
              )}
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <VStack spacing={5} align="stretch">
                {!decision && <MeetingOriginBanner />}

                <FormControl isInvalid={!!errors.title}>
                  <FormLabel>{t('DecisionEditModal.title')}</FormLabel>
                  <Input
                    {...register('title')}
                    placeholder={t('DecisionEditModal.titlePlaceholder')}
                    autoFocus
                  />
                </FormControl>

                <CircleFormController />

                <FormControl isInvalid={!!errors.description}>
                  <FormLabel>{t('DecisionEditModal.description')}</FormLabel>
                  <EditorController
                    name="description"
                    placeholder={t('DecisionEditModal.descriptionPlaceholder')}
                    control={control}
                  />
                </FormControl>

                <Controller
                  name="visibility"
                  control={control}
                  render={({ field }) => (
                    <VisibilityField
                      objectType="decision"
                      circleId={circleId}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />

                <Collapse in={!isVisibilityAllowed}>
                  <Alert status="warning">
                    <AlertIcon />
                    <AlertDescription>
                      {t('VisibilityField.notAllowedRead')}
                    </AlertDescription>
                  </Alert>
                </Collapse>

                <Box textAlign="right" mt={2}>
                  <Button
                    colorScheme="blue"
                    type="submit"
                    isDisabled={!isVisibilityAllowed}
                  >
                    {t(decision ? 'common.save' : 'common.create')}
                  </Button>
                </Box>
              </VStack>
            </ModalBody>
          </form>
        </ModalContent>
      </Modal>
    </FormProvider>
  )
}
